import { useState } from "react";

import { useProbe, useSubmitProbe } from "@/api/queries";
import { useCan } from "@/auth/member";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label, Textarea } from "@/components/ui/input";
import { Callout, Empty, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { cn, formatScore } from "@/lib/utils";

/**
 * Similarity probe — DESIGN §14.4.
 *
 * Type a question that is not in the bank and watch what the bank would do with it:
 * enriched to a concept signature, embedded, then compared against its nearest
 * neighbours by the §9.2 cascade. It is the tool for deciding whether a threshold
 * change was an improvement, and the only place the cascade can be watched reasoning
 * about a question someone just made up.
 *
 * Two model calls, so it is a job (§8 invariant 01) — submit returns an id and this
 * screen polls it. The Streamlit version ran both calls inside the page render.
 */
const TH = "text-bank-muted px-3 py-2 font-mono text-[10.5px] font-medium tracking-[0.07em] uppercase";

const K_MIN = 3;
const K_MAX = 25;

export default function Probe() {
  const [text, setText] = useState("");
  const [k, setK] = useState(8);
  const [jobId, setJobId] = useState<number | null>(null);

  const submit = useSubmitProbe();
  const probe = useProbe(jobId);

  const job = probe.data?.job;
  const result = probe.data?.result;
  const waiting = job?.status === "queued" || job?.status === "running";
  const busy = submit.isPending || waiting;
  const canProbe = useCan("probe");

  function run() {
    if (!text.trim()) return;
    submit.mutate({ text: text.trim(), k }, { onSuccess: (accepted) => setJobId(accepted.job_id) });
  }

  // Every probe is two model calls on a 20-a-day quota, so it is a Maintainer's tool, not a Viewer's.
  if (!canProbe) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <h1 className="text-base font-semibold tracking-tight">Similarity probe</h1>
        <Callout>
          Probing spends model quota, so it is for maintainers and admins. Your role can read the bank.
        </Callout>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      <h1 className="text-base font-semibold tracking-tight">Similarity probe</h1>

      <ErrorNote error={submit.error ?? probe.error} />

      <Panel className="p-3">
        <Label htmlFor="probe-text">
          A question — enriched, embedded and compared with the bank on the same path a real ingest takes
          (§2.1). Two model calls, so it runs in the worker.
        </Label>
        <Textarea
          id="probe-text"
          value={text}
          placeholder="How does Node run callbacks without blocking?"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Enter alone inserts a newline: this is a textarea because a probe is
            // sometimes a long question, and swallowing Enter would fight that.
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) run();
          }}
        />
        <div className="mt-3 flex flex-wrap items-end gap-4">
          <div className="min-w-52">
            <Label htmlFor="probe-k">Neighbours: {k}</Label>
            <input
              id="probe-k"
              type="range"
              min={K_MIN}
              max={K_MAX}
              value={k}
              onChange={(e) => setK(Number(e.target.value))}
              className="accent-bank-accent w-full"
            />
          </div>
          <Button variant="primary" disabled={busy || !text.trim()} onClick={run}>
            {busy ? "Probing…" : "Probe"}
          </Button>
          {job && (
            <p className="text-bank-muted flex items-center gap-1.5 font-mono text-[11px]">
              job #{job.id} <Badge value={job.status} />
              {/* A worker that hits the daily cap requeues the job rather than failing
                  it (§8.6), so a probe can legitimately sit here until quota returns.
                  Metrics shows what is left of it. */}
              {job.status === "queued" && "waiting for the worker"}
            </p>
          )}
        </div>
        {job?.status === "failed" && <ErrorNote error={job.error ?? "the probe failed"} />}
      </Panel>

      {result && (
        <>
          <Panel>
            <PanelHeader title="What the pipeline made of it" />
            <div className="space-y-2 px-4 py-3">
              <div>
                <p className="text-bank-muted mb-1 font-mono text-[10.5px] tracking-[0.07em] uppercase">
                  Concept signature — this is what gets embedded
                </p>
                <p className="border-bank-border bg-bank-surface-2 rounded-md border px-2.5 py-2 font-mono text-xs">
                  {result.concept_signature}
                </p>
              </div>
              <dl className="text-bank-muted flex flex-wrap gap-x-5 gap-y-1 pt-1 font-mono text-[11px]">
                <div>
                  <dt className="inline opacity-75">concept</dt>{" "}
                  <dd className="text-bank-ink inline">{result.primary_concept ?? "—"}</dd>
                </div>
                <div>
                  <dt className="inline opacity-75">stack</dt>{" "}
                  <dd className="text-bank-ink inline">{result.stack_path ?? "—"}</dd>
                </div>
                <div>
                  <dt className="inline opacity-75">type</dt>{" "}
                  <dd className="text-bank-ink inline">{result.question_type ?? "—"}</dd>
                </div>
                <div>
                  <dt className="inline opacity-75">difficulty</dt>{" "}
                  <dd className="text-bank-ink inline">{result.difficulty_score ?? "—"}</dd>
                </div>
                <div>
                  <dt className="inline opacity-75">entities</dt>{" "}
                  <dd className="text-bank-ink inline">
                    {result.entities.length > 0 ? result.entities.join(", ") : "—"}
                  </dd>
                </div>
              </dl>
            </div>
          </Panel>

          <Panel>
            <PanelHeader
              title={`Nearest ${result.neighbours.length} in the bank`}
              actions={
                <span className="text-bank-muted text-xs">
                  ordered by cosine distance; verdict is what §9.2 would decide
                </span>
              }
            />
            {result.neighbours.length === 0 ? (
              <Empty>
                Nothing to compare against — the bank has no questions embedded by the current model (§5.6).
              </Empty>
            ) : (
              <div className="overflow-x-auto">
                {/* The table sets its own minimum and the container scrolls. Without it
                    the browser settles the eight columns by squeezing the two that carry
                    prose — the question wraps to ten lines and the reasoning runs off
                    the panel, which is the pair someone reads a probe for. */}
                <table className="w-full min-w-[62rem] text-sm">
                  <thead className="bg-bank-surface-2 border-bank-border text-bank-muted border-b text-left">
                    <tr>
                      <th className={cn(TH, "w-10")}>#</th>
                      <th className={cn(TH, "w-[32%]")}>question</th>
                      <th className={TH}>status</th>
                      <th className={cn(TH, "text-right")}>cosine</th>
                      <th className={cn(TH, "text-right")}>trigram</th>
                      <th className={cn(TH, "text-right")}>entities</th>
                      <th className={TH}>verdict</th>
                      <th className={cn(TH, "w-[26%]")}>why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.neighbours.map((n) => (
                      <tr
                        key={n.id}
                        className="border-bank-border hover:bg-bank-surface-2 border-t align-top transition-colors"
                      >
                        <td className="text-bank-muted px-3 py-2 font-mono text-[11px] tabular-nums">
                          {n.id}
                        </td>
                        <td className="question-text px-3 py-2 text-[13px]">{n.question_text}</td>
                        <td className="px-3 py-2">
                          <Badge value={n.status} />
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[11px] whitespace-nowrap tabular-nums">
                          {formatScore(n.cosine)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[11px] whitespace-nowrap tabular-nums">
                          {formatScore(n.trigram)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-[11px] whitespace-nowrap tabular-nums">
                          {formatScore(n.entity_jaccard)}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">
                          {/* No verdict means the cascade ran out of cheap answers and
                              would spend an adjudication call here (§9.2 layer 4). */}
                          {n.verdict ? <Badge value={n.verdict} /> : <span>→ LLM</span>}
                          <span className="text-bank-muted ml-1 font-mono text-[11px]">L{n.layer}</span>
                        </td>
                        <td className="text-bank-muted px-3 py-2 text-xs leading-relaxed">{n.reasoning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
